/**
 * @file DeliveryAgreementSummary.tsx
 * @description Статус и суммы заявки «доставка по договорённости»
 * (F30, phase19.15 шаг 4).
 *
 * Отделено от `DeliveryAgreementSection`: секция занимается действиями
 * (форма цены, отправка письма), а здесь — только то, что менеджер читает
 * глазами. Главный элемент — зелёная отметка «клиент подтвердил»: это
 * доказательство по ПП РФ 2463 п. 13, и оно обязано бросаться в глаза.
 */

import { CheckCircle2, Clock, Link2 } from "lucide-react";

import {
  DELIVERY_AGREEMENT_STATUS_META,
  deliveryAgreementStatusText,
  type DeliveryAgreementDto,
} from "../../lib/api-delivery-agreement";
import { formatMoney } from "../../lib/api-orders";
import { receiptSummaryText } from "../../lib/receipt-method";
import { formatShopDateTime } from "../../lib/shop-time";
import { Badge } from "../ui/badge";
import { SectionLabel } from "../ui/SectionLabel";

interface Props {
  agreement: DeliveryAgreementDto;
  /**
   * Печатать строку «Ссылка подтверждения». `false` — роль не ведёт заказы:
   * диктовать клиенту ссылку ей нечего (ревью 5.2). По умолчанию `true` —
   * сводку читает менеджер заказов.
   */
  confirmLink?: boolean;
}

export function DeliveryAgreementSummary({ agreement: a, confirmLink = true }: Props) {
  const meta = DELIVERY_AGREEMENT_STATUS_META[a.status];
  const statusText = deliveryAgreementStatusText(
    a.status,
    a.expiresAt ? formatShopDateTime(a.expiresAt) : null,
  );
  // Бейдж и подпись рядом — значит подпись обязана добавлять смысл. Совпали
  // (сервер прислал состояние без своего объяснения) — печатаем только бейдж,
  // а не «Заявка отменена · Заявка отменена» (дефект QA 5.1).
  const showStatusText = statusText.trim() !== meta.badge.trim();

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={meta.variant} className={meta.className}>
          {meta.badge}
        </Badge>
        {showStatusText ? (
          <span className="text-sm text-on-surface-variant">{statusText}</span>
        ) : null}
      </div>

      {a.confirmedAt ? (
        // Отметка о согласии клиента (требование пользователя 2026-08-15):
        // сумма и время — ровно то, что подтверждено, и то, чем менеджер
        // объяснит цену при разборе спора.
        <p className="flex items-start gap-2 rounded-md bg-primary/12 px-3 py-2 text-sm font-medium text-primary">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
          <span className="min-w-0 break-words">
            Клиент подтвердил итоговую стоимость {formatMoney(a.totals.totalGross)} ₽ —{" "}
            {formatShopDateTime(a.confirmedAt)}
          </span>
        </p>
      ) : null}

      <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
        {/* Суммы — только через formatMoney: сервер отдаёт decimal-строку
            «490.00», а менеджер читает рубли по-русски («490,00 ₽»). */}
        <Cell label="Товары" value={`${formatMoney(a.totals.itemsTotal)} ₽`} />
        <Cell label="Доставка" value={deliveryCellText(a)} />
        <Cell label="Итого" value={`${formatMoney(a.totals.totalGross)} ₽`} highlight />
        <Cell label="Чем везём" value={receiptLabel(a)} />
      </dl>

      {/* Адрес и график — там же, где суммы: менеджер диктует их клиенту по
          телефону и не должен искать их во вкладке «Отгрузки». */}
      {a.receipt ? (
        <p className="text-sm text-on-surface-variant">
          {a.receipt.method === "PICKUP"
            ? [
                a.receipt.pickup?.name,
                a.receipt.pickup?.address ?? "адрес пункта не заполнен",
                a.receipt.pickup?.workingHours
                  ? `график ${a.receipt.pickup.workingHours}`
                  : "график уточнит менеджер",
              ]
                .filter(Boolean)
                .join(" · ")
            : (a.receipt.address ?? "адрес не указан")}
        </p>
      ) : null}

      <div className="flex flex-col gap-1 text-xs text-on-surface-variant">
        {/* Не «принята»: сервер перезаписывает `requestedAt` при отзыве
            предложения (72-часовой срок расчёта стартует заново), и дата
            разошлась бы с «Создан» в Обзоре. Пишем честное «в работе с». */}
        {a.requestedAt ? (
          <span title="Дата создания или последнего обновления заявки — от неё считается срок расчёта">
            В работе с {formatShopDateTime(a.requestedAt)}
          </span>
        ) : null}
        {a.calculatedAt ? (
          <span className="flex items-center gap-1">
            <Clock className="size-3 shrink-0" />
            Предложение отправлено {formatShopDateTime(a.calculatedAt)}
            {a.expiresAt ? ` · действует до ${formatShopDateTime(a.expiresAt)}` : ""}
          </span>
        ) : null}
        {confirmLink && a.confirmUrl ? (
          <a
            href={a.confirmUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-primary hover:underline"
          >
            <Link2 className="size-3 shrink-0" />
            Ссылка подтверждения (можно продиктовать клиенту)
          </a>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Строка «Доставка» в сводке: ноль ноль-рублёвый бывает по трём разным
 * причинам, и путать их нельзя.
 *
 * Самовывоз — доставки НЕТ («не нужна», как «не выставляется (самовывоз)» в
 * форме создания); своими силами за 0 ₽ — «бесплатно» (осознанное решение
 * менеджера); суммы нет вовсе — «не выставлена», это наша недоделка.
 * «0,00 ₽» читалось как «забыли поставить цену» (замечание 2026-09-21).
 */
function deliveryCellText(a: DeliveryAgreementDto): string {
  if (a.receipt?.method === "PICKUP") return "не нужна";
  const amount = a.totals.deliveryAmount;
  if (amount === null) return "не выставлена";
  return Number(amount) === 0 ? "бесплатно" : `${formatMoney(amount)} ₽`;
}

/**
 * «Чем везём»: способ получения из заявки, а если сервер его ещё не отдаёт —
 * прежняя подпись позиции-услуги. Реестр исходов один на форму и на сводку,
 * поэтому «Самовывоз» тут не может разойтись с «Самовывозом» в форме.
 */
function receiptLabel(a: DeliveryAgreementDto): string {
  if (!a.receipt) return a.deliveryLabel ?? "—";
  const amount = a.receipt.deliveryAmount;
  return receiptSummaryText(a.receipt.method, {
    // Подписи берём у сервера: реестр исходов там первичен, а клиентский —
    // его зеркало. Разойдутся — клиент увидит в письме одно, менеджер другое.
    label: a.receipt.label,
    carrierName: a.receipt.carrierName,
    amount: amount === null ? null : Number(amount),
  });
}

function Cell({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="min-w-0">
      <SectionLabel as="dt">{label}</SectionLabel>
      <dd
        className={`mt-0.5 break-words ${highlight ? "font-semibold text-primary" : "font-medium"}`}
      >
        {value}
      </dd>
    </div>
  );
}
